import React from 'react';

import { useIntl } from 'react-intl';
import { Row, Col, Image } from 'react-bootstrap';

import { getDeviceImage } from 'utils/Devices';

type DeviceListKey = 'sku' | 'deviceId';

interface IDevicesListProps {
  selectedDevices: {
    [key: string]: number;
  };
  devices: (IDeviceOrderingCatalogItem | IDeviceListItem)[];
  displayQuantity?: boolean;
  deviceKey?: DeviceListKey;
  displayPatientData?: boolean;
  dataTestId?: string;
}

const getDisplayName = (device: IDeviceOrderingCatalogItem | IDeviceListItem): string => {
  if ('displayName' in device) return device.displayName;
  return device.deviceOrdering?.displayName ?? '';
};

const getImei = (device: IDeviceOrderingCatalogItem | IDeviceListItem): string | undefined => {
  if ('imei' in device) return device.imei;
  return undefined;
};

const getKey = (
  device: IDeviceOrderingCatalogItem | IDeviceListItem,
  key: DeviceListKey
): string => {
  if (key === 'sku' && 'sku' in device) return (device as IDeviceOrderingCatalogItem).sku ?? '';
  if (key === 'deviceId' && 'deviceId' in device) return (device as IDeviceListItem).deviceId;
  return '';
};

/** The selected cuff option of a catalogue device, if one of the selected SKUs is one of its options. */
const getSelectedOption = (
  device: IDeviceOrderingCatalogItem | IDeviceListItem,
  selectedSkus: string[]
): IDeviceOrderingOption | undefined => {
  if (!('options' in device) || !Array.isArray(device.options)) return undefined;
  return (device.options as IDeviceOrderingOption[]).find((option) =>
    selectedSkus.includes(option.sku)
  );
};

const DevicesList = ({
  selectedDevices,
  devices,
  deviceKey = 'sku',
  displayPatientData,
  displayQuantity = false,
  dataTestId
}: IDevicesListProps) => {
  const intl = useIntl();
  const selectedSkus = Object.keys(selectedDevices);
  return (
    <Row className="row-eq-height" data-test={dataTestId}>
      {Object.keys(selectedDevices).map((selected: string) => {
        const device = devices.find((d) => getKey(d, deviceKey) === selected);
        if (device) {
          const displayName = getDisplayName(device);
          return (
            <Col
              key={getKey(device, deviceKey)}
              md={6}
              className="mb-2"
              data-test-value={displayName}
            >
              <div className="bg-blue-light p-3 d-flex h-100">
                <div className="mw-80 d-flex align-items-center">
                  <Image
                    fluid
                    src={getDeviceImage(displayName)}
                    alt={intl.formatMessage({ id: displayName || '' })}
                  />
                </div>
                <div className="d-flex flex-column justify-content-center">
                  <div className="ps-3">
                    <p className="m-0">{intl.formatMessage({ id: displayName || '' })}</p>
                    {(() => {
                      const option = getSelectedOption(device, selectedSkus);
                      return option ? (
                        <p
                          className="text-sm text-secondary m-0"
                          data-test={`kit-cuff-${option.sku}`}
                        >
                          {intl.formatMessage(
                            { id: 'devices.kitCuff' },
                            {
                              cuff: `${option.sku} ${intl.formatMessage({ id: option.displayName })}`
                            }
                          )}
                        </p>
                      ) : null;
                    })()}
                  </div>
                  <>
                    {displayPatientData && (
                      <p className="text-sm font-italic m-0 ps-3">
                        {intl.formatMessage({ id: 'imeiKey' }) +
                          ': ' +
                          (getImei(device) || intl.formatMessage({ id: 'imeiNotAvailableKey' }))}
                      </p>
                    )}
                  </>
                </div>
                {displayQuantity && (
                  <div className="d-flex flex-column justify-content-center flex-shrink-0 ms-auto">
                    <p className="m-0">
                      {intl.formatMessage({ id: 'qtyKey' }) + ': '}
                      <span className="font-weight-bold"> {selectedDevices[selected]}</span>
                    </p>
                  </div>
                )}
              </div>
            </Col>
          );
        }
      })}
    </Row>
  );
};

export default DevicesList;
